// A mock-up of every court's LED scoreboard, for Court Rentals → Policy.
//
// Fed by the scoreboardPreview callable, which runs the same loader and rules
// as the endpoint vb-scores calls — so this shows what the boards are sent,
// not a second guess at it. The server returns each court's day as spans; the
// time picker just chooses a span, so scrubbing through the day needs no
// further calls.
//
// Row 1 is ours only when an event ticked "show the description" (`header`).
// Otherwise vb-scores draws its own heading, which this can only stand in for:
// "RESERVED FOR" while a court is reserved, a dim placeholder otherwise,
// because what vb-scores shows for the next or open states is theirs to choose.
import React, { useCallback, useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { logError } from '../utils/errorLogger';
import {
  Paper, Typography, Box, Grid, TextField, Button, Alert, CircularProgress,
} from '@mui/material';

const LED_GREEN = '#3DFF7A';
const LED_RED = '#FF3B3B';

// "17:05" → "5:05", as the board's clock shows it.
function clock(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return '';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')}`;
}

function spanAt(schedule, hhmm) {
  return (schedule || []).find(s => s.from <= hhmm && hhmm < s.to) || null;
}

function Board({ court, span, time }) {
  const vbHeading = !span?.header;
  const row1 = span?.header
    || (span?.mode === 'reserved' ? 'RESERVED FOR' : 'vb-scores heading');
  const row1Dim = vbHeading && span?.mode !== 'reserved';
  const courtLabel = court.replace('court-', 'Court ');
  const until = span && span.to !== '24:00' ? `until ${clock(span.to)}` : 'rest of day';

  return (
    <Box>
      <Box sx={{
        backgroundColor: '#0B0B0B', borderRadius: 1.5, border: '3px solid #222',
        p: 1.5, minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center',
        fontFamily: '"Courier New", Menlo, monospace', textAlign: 'center',
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
        backgroundSize: '5px 5px',
      }}>
        <Typography sx={{ color: LED_RED, fontFamily: 'inherit', fontWeight: 700, fontSize: '1.4rem', lineHeight: 1.1, mb: 1,
          textShadow: `0 0 6px ${LED_RED}` }}>
          {clock(time)}
        </Typography>
        {[
          { text: row1, dim: row1Dim, caps: vbHeading },
          { text: span?.lines?.[0] || '' },
          { text: span?.lines?.[1] || '' },
        ].map((r, i) => (
          <Typography key={i} sx={{
            color: r.dim ? 'rgba(61,255,122,0.35)' : LED_GREEN,
            fontFamily: 'inherit', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1.35,
            minHeight: '1.3em', whiteSpace: 'pre', fontStyle: r.dim ? 'italic' : 'normal',
            textShadow: r.dim ? 'none' : `0 0 5px ${LED_GREEN}`,
            textTransform: r.caps && !r.dim ? 'uppercase' : 'none',
          }}>
            {r.text}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, px: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E3A5F' }}>{courtLabel}</Typography>
        <Typography variant="caption" sx={{ color: '#888' }}>
          {span ? `${span.mode} · ${until}` : ''}
          {span?.header ? ' · event heading' : ''}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ScoreboardPreviewPanel() {
  const [data, setData] = useState(null);
  // Whether anything is actually CALLING the API, which the preview cannot
  // show: it renders what a board would be sent, not whether a board asked.
  // scoreboardDisplay records a row per court; see noteScoreboardAccess.
  const [access, setAccess] = useState(null);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (forDate) => {
    setLoading(true); setError('');
    try {
      const fn = httpsCallable(getFunctions(), 'scoreboardPreview');
      const res = await fn(forDate ? { date: forDate } : {});
      setData(res.data);
      setDate(res.data.date);
      // Today: the board as it stands now. Another day: early evening, when
      // the courts are busiest, so there is something to look at.
      setTime(t => (t && forDate) ? t : (res.data.date === res.data.today ? res.data.now : '18:00'));
    } catch (e) {
      logError('ScoreboardPreviewPanel', e.message, e.stack);
      setError(e.message || 'Could not load the scoreboard preview.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(null); }, [load]);

  useEffect(() => {
    getDocs(collection(db, 'scoreboardAccess'))
      .then(snap => setAccess(snap.docs.map(d => ({ id: d.id, ...d.data() }))))
      // Admin-only by rule, and an empty collection is the answer "nobody has
      // called yet" rather than a failure — so a miss is not an error here.
      .catch(() => setAccess([]));
  }, []);

  const since = (ts) => {
    const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
    if (!d || isNaN(d)) return null;
    const mins = Math.round((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 36) return `${hrs} hr ago`;
    return `${Math.round(hrs / 24)} days ago`;
  };

  const isToday = data && date === data.today;

  return (
    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #eee', mb: 3, backgroundColor: '#f9fafb' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
        Scoreboard Preview
      </Typography>
      <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
        What each court's board is sent, at the date and time you pick. The top line is vb-scores' own heading unless
        an event ticked "show the description" — dim text marks where vb-scores chooses what to show.
      </Typography>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Is anyone asking? Until this existed the only way to know was Cloud
          Logging, where a wrong key and no board at all look much the same. */}
      {access && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1.5, backgroundColor: 'white', border: '1px solid #eee' }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E3A5F', display: 'block', mb: 0.5 }}>
            API calls
          </Typography>
          {access.length === 0 ? (
            <Typography variant="caption" sx={{ color: '#B26A00' }}>
              Nothing has called the scoreboard API yet. The boards are still showing vb-scores' own data.
            </Typography>
          ) : (
            access
              .slice()
              .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }))
              .map(a => {
                const okAgo = since(a.lastOkAt);
                const seenAgo = since(a.lastSeenAt);
                // A board that once worked and is now being refused is the case
                // worth shouting about: the key changed, or the wrong one shipped.
                const refused = a.lastOutcome !== 'ok';
                return (
                  <Typography key={a.id} variant="caption"
                    sx={{ display: 'block', color: refused ? '#922B21' : '#1E6B3C' }}>
                    {a.id}: {refused
                      ? `last call ${seenAgo} was refused (${a.lastOutcome})`
                      : `polled ${okAgo || seenAgo}`}
                    {a.count_ok ? ` · ${a.count_ok} ok` : ''}
                    {a.count_bad_key ? ` · ${a.count_bad_key} bad key` : ''}
                  </Typography>
                );
              })
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <TextField size="small" type="date" label="Date" value={date}
          onChange={e => { setDate(e.target.value); if (e.target.value) load(e.target.value); }}
          InputLabelProps={{ shrink: true }} sx={{ backgroundColor: 'white', width: 170 }} />
        <TextField size="small" type="time" label="Time" value={time}
          onChange={e => setTime(e.target.value)}
          InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }}
          sx={{ backgroundColor: 'white', width: 130 }} />
        <Button size="small" variant="outlined" disabled={loading}
          onClick={() => { setTime(''); load(null); }}>
          Now
        </Button>
        <Button size="small" disabled={loading} onClick={() => load(date || null)}>
          Refresh
        </Button>
        {loading && <CircularProgress size={18} />}
        {data && !isToday && (
          <Typography variant="caption" sx={{ color: '#888' }}>Previewing {date}, not today.</Typography>
        )}
        {data && (
          <Typography variant="caption" sx={{ color: '#888', ml: 'auto' }}>
            Changeover {data.changeoverMinutes} min
          </Typography>
        )}
      </Box>

      {data && (
        <Grid container spacing={2}>
          {data.courts.map(c => (
            <Grid key={c.court} size={{ xs: 12, sm: 6, md: 4 }}>
              <Board court={c.court} span={spanAt(c.schedule, time || '00:00')} time={time} />
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}
